import { useCallback, useEffect, useRef } from "react";
import { MIN_BARCODE_LENGTH, shouldHandleAsBarcodeScan } from "../utils/barcodeUtils";
import {
	DUPLICATE_SCAN_MS,
	SCAN_IDLE_MS,
	posScanLog,
	shouldAutoProcessScan,
} from "../utils/posScannerUtils";

/**
 * POS product search + barcode wedge input (desktop HID + Android Chrome / Scandit).
 * Uses input + idle timing; Enter is fallback only.
 */
export const usePosBarcodeInput = ({
	onScan,
	onValueChange,
	enabled = true,
	minLength = MIN_BARCODE_LENGTH,
	inputRef,
	scanIdleMs = SCAN_IDLE_MS,
}) => {
	const processingRef = useRef(false);
	const idleTimerRef = useRef(null);
	const trackedValueRef = useRef("");
	const inputEventsRef = useRef([]);
	const keyTimesRef = useRef([]);
	const lastProcessedRef = useRef({ value: "", at: 0 });
	const composingRef = useRef(false);

	const refocusSearch = useCallback(() => {
		const el = inputRef?.current;
		if (!el) return;
		const run = () => {
			try {
				el.focus({ preventScroll: true });
			} catch {
				el.focus();
			}
			if (typeof el.setSelectionRange === "function") {
				const len = el.value?.length ?? 0;
				el.setSelectionRange(len, len);
			}
			posScanLog("refocus search input");
		};
		requestAnimationFrame(run);
		setTimeout(run, 50);
	}, [inputRef]);

	const clearIdleTimer = useCallback(() => {
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current);
			idleTimerRef.current = null;
		}
	}, []);

	const tryProcessScan = useCallback(
		async (rawValue, { force = false } = {}) => {
			if (!enabled || processingRef.current) return false;

			const trimmed = String(rawValue ?? "").trim();
			if (trimmed.length < minLength) return false;

			const now = Date.now();
			if (
				!force &&
				lastProcessedRef.current.value === trimmed &&
				now - lastProcessedRef.current.at < DUPLICATE_SCAN_MS
			) {
				posScanLog("duplicate scan ignored (double-fire)", trimmed);
				return false;
			}

			if (
				!force &&
				!shouldAutoProcessScan(trimmed, inputEventsRef.current, keyTimesRef.current)
			) {
				posScanLog("idle skip (manual typing)", trimmed);
				return false;
			}

			processingRef.current = true;
			lastProcessedRef.current = { value: trimmed, at: now };
			clearIdleTimer();
			posScanLog("processing barcode", trimmed, { force });

			try {
				await onScan?.(trimmed);
				return true;
			} catch (err) {
				console.error("[POS-Scan] onScan error:", err);
				return false;
			} finally {
				processingRef.current = false;
				inputEventsRef.current = [];
				keyTimesRef.current = [];
				trackedValueRef.current = "";
				refocusSearch();
			}
		},
		[clearIdleTimer, enabled, minLength, onScan, refocusSearch]
	);

	const scheduleScanAfterIdle = useCallback(
		(value) => {
			clearIdleTimer();
			idleTimerRef.current = setTimeout(() => {
				tryProcessScan(value);
			}, scanIdleMs);
		},
		[clearIdleTimer, scanIdleMs, tryProcessScan]
	);

	const recordValueChange = useCallback(
		(nextValue, prevValue) => {
			const now = Date.now();
			const delta = Math.max(0, nextValue.length - prevValue.length);
			if (delta > 0) {
				inputEventsRef.current.push({ at: now, delta });
				if (inputEventsRef.current.length > 40) {
					inputEventsRef.current = inputEventsRef.current.slice(-40);
				}
			}
			trackedValueRef.current = nextValue;
			posScanLog("input change", { nextValue, delta, composing: composingRef.current });

			if (!composingRef.current) {
				scheduleScanAfterIdle(nextValue);
			}
		},
		[scheduleScanAfterIdle]
	);

	const handleChange = useCallback(
		(event) => {
			const prev = trackedValueRef.current;
			const next = event.target.value ?? "";
			onValueChange?.(next);
			recordValueChange(next, prev);
		},
		[onValueChange, recordValueChange]
	);

	const handleInput = useCallback(
		(event) => {
			const prev = trackedValueRef.current;
			const next = event.target?.value ?? "";
			if (next === prev) return;
			onValueChange?.(next);
			recordValueChange(next, prev);
			posScanLog("native input event", next);
		},
		[onValueChange, recordValueChange]
	);

	const handleKeyDown = useCallback(
		(event) => {
			if (!enabled) return;

			if (event.key === "Enter") {
				const value = (event.target?.value ?? "").trim();
				if (value.length < minLength) return;

				const timestamps = [...keyTimesRef.current];
				const forceScan =
					/^\d{8,}$/.test(value) ||
					shouldHandleAsBarcodeScan(value, timestamps) ||
					shouldAutoProcessScan(value, inputEventsRef.current, timestamps, {
						force: false,
					});

				if (forceScan) {
					event.preventDefault();
					event.stopPropagation();
					clearIdleTimer();
					keyTimesRef.current = [];
					tryProcessScan(value, { force: true });
				}
				return;
			}

			if (event.key.length === 1) {
				const now = Date.now();
				keyTimesRef.current.push(now);
				if (keyTimesRef.current.length > 64) {
					keyTimesRef.current = keyTimesRef.current.slice(-64);
				}
			}
		},
		[clearIdleTimer, enabled, minLength, tryProcessScan]
	);

	const handleCompositionStart = useCallback(() => {
		composingRef.current = true;
		clearIdleTimer();
		posScanLog("composition start");
	}, [clearIdleTimer]);

	const handleCompositionEnd = useCallback(
		(event) => {
			composingRef.current = false;
			const next = event.target?.value ?? "";
			const prev = trackedValueRef.current;
			onValueChange?.(next);
			recordValueChange(next, prev);
			posScanLog("composition end", next);
		},
		[onValueChange, recordValueChange]
	);

	useEffect(
		() => () => {
			clearIdleTimer();
		},
		[clearIdleTimer]
	);

	const getSearchInputProps = useCallback(
		() => ({
			onChange: handleChange,
			onInput: handleInput,
			onKeyDown: handleKeyDown,
			onCompositionStart: handleCompositionStart,
			onCompositionEnd: handleCompositionEnd,
			autoComplete: "off",
			autoCorrect: "off",
			autoCapitalize: "off",
			spellCheck: false,
			enterKeyHint: "search",
		}),
		[
			handleChange,
			handleCompositionEnd,
			handleCompositionStart,
			handleInput,
			handleKeyDown,
		]
	);

	return {
		getSearchInputProps,
		refocusSearch,
		tryProcessScan,
		isProcessing: () => processingRef.current,
	};
};

export default usePosBarcodeInput;
